<template>
    <BaseButton v-if="showTrigger" :theme="theme" :size="size" :disabled="disabled" @click="openDialog"
        ><slot>{{ t("teamPerspective.leave.action") }}</slot></BaseButton
    >
    <ConfirmationDialog
        v-model:open="open"
        :title="t('teamPerspective.leave.title', { name: teamName })"
        :text="t('teamPerspective.leave.description')"
        :description="t('teamPerspective.leave.description')"
        :confirm-label="busy ? t('teamPerspective.leave.pending') : t('teamPerspective.leave.action')"
        :cancel-label="t('teamPerspective.cancel')"
        :confirm-disabled="busy"
        :cancel-disabled="busy"
        :close-on-confirm="false"
        :return-focus-to="returnFocusTo"
        dialog-content-class="fs-mask"
        @confirm="leave">
        <template #content>
            <BaseNotification v-if="error" theme="error" :text="error" hide-dismiss />
        </template>
    </ConfirmationDialog>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import BaseButton, { type ButtonSize, type ButtonTheme } from "~vue/components/BaseButton.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import { TEAM_INTERACTION_SOURCE, teamInteractionRequest, type TeamInteractionSource } from "~vue/Teams/teamInteraction";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { type ComponentPublicInstance, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const {
    source = TEAM_INTERACTION_SOURCE.TEAM_PAGE,
    teamName,
    leaveUrl,
    disabled = false,
    theme = "secondary",
    size = "sm",
    showTrigger = true,
    returnFocusTo = null,
} = defineProps<{
    source?: TeamInteractionSource;
    teamName: string;
    leaveUrl: string;
    disabled?: boolean;
    theme?: ButtonTheme;
    size?: ButtonSize;
    // False when a parent (e.g. a menu item) opens the confirmation through v-model:open instead of this button.
    showTrigger?: boolean;
    // Where focus goes when the dialog closes; needed when showTrigger is false, since there is no button to return to.
    returnFocusTo?: HTMLElement | ComponentPublicInstance | null;
}>();
const emit = defineEmits<{ left: [] }>();
const { t } = useI18n();
const open = defineModel<boolean>("open", { default: false });
const busy = ref(false);
const error = ref("");

function openDialog() {
    open.value = true;
}
watch(open, (isOpen) => {
    if (isOpen) {
        logUserInteraction(AmplitudeEventNames.TEAM_LEAVE_STARTED, { source });
        error.value = "";
    }
});

async function leave() {
    if (busy.value) {
        return;
    }
    busy.value = true;
    error.value = "";
    try {
        const { error: requestError, statusCode } = await useFetch(leaveUrl, teamInteractionRequest(source)).post();
        if (requestError.value) {
            error.value = statusCode.value === 409 ? t("teamPage.leadLeaveHelp") : t("teamPerspective.leave.error");
            return;
        }
        open.value = false;
        emit("left");
    } catch {
        error.value = t("teamPerspective.leave.error");
    } finally {
        busy.value = false;
    }
}
</script>
